import { useMemo, useState } from "react";
import {
  ColumnDef,
  FilterFn,
  PaginationState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button, Table } from "react-bootstrap";

import FeatureFlagListPagination from "./FeatureFlagListPagination";
import FeatureFlagListToolbar from "./FeatureFlagListToolbar";
import FormatFeatureFlagDate from "./FormatFeatureFlagDate";

/** One tenant or facility assignment shown in the details panel tables. */
export interface FeatureFlagAssignmentRow {
  id: number;
  name: string;
  startDate: Date | null;
  endDate: Date | null;
}

interface FeatureFlagAssignmentTableProps {
  /** "Tenant" or "Facility"; used for the name column, search label, and add button. */
  itemLabel: string;
  rows: FeatureFlagAssignmentRow[];
  /** Unique id prefix so inputs stay unique when several tables are on the page. */
  idPrefix: string;
}

const nameFilter: FilterFn<FeatureFlagAssignmentRow> = (row, _columnId, filterValue) => {
  const query = String(filterValue ?? "").trim().toLowerCase();
  return !query || String(row.original.name ?? "").toLowerCase().includes(query);
};

/** Sorts missing dates before every real date. */
function compareNullableDates(a: Date | null, b: Date | null): number {
  const aTimestamp = a ? a.getTime() : Number.NEGATIVE_INFINITY;
  const bTimestamp = b ? b.getTime() : Number.NEGATIVE_INFINITY;
  return aTimestamp === bTimestamp ? 0 : aTimestamp > bTimestamp ? 1 : -1;
}

/**
 * Tenant/facility assignment table for the feature flag details panel, with
 * search, per-column sorting, and pagination. The Edit, Delete, and Add
 * buttons are display-only until assignment management is implemented.
 */
function FeatureFlagAssignmentTable({
  itemLabel,
  rows,
  idPrefix,
}: FeatureFlagAssignmentTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  const columns = useMemo<ColumnDef<FeatureFlagAssignmentRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: itemLabel,
        enableSorting: true,
        enableGlobalFilter: true,
      },
      {
        accessorKey: "startDate",
        header: "Start Date",
        enableSorting: true,
        enableGlobalFilter: false,
        sortingFn: (rowA, rowB, columnId) =>
          compareNullableDates(
            rowA.getValue<Date | null>(columnId),
            rowB.getValue<Date | null>(columnId)
          ),
        cell: ({ getValue }) => FormatFeatureFlagDate(getValue() as Date | null),
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        enableSorting: true,
        enableGlobalFilter: false,
        sortingFn: (rowA, rowB, columnId) =>
          compareNullableDates(
            rowA.getValue<Date | null>(columnId),
            rowB.getValue<Date | null>(columnId)
          ),
        cell: ({ getValue }) => FormatFeatureFlagDate(getValue() as Date | null),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        enableGlobalFilter: false,
        cell: ({ row }) => (
          <div className="rowActions">
            <div className="rowActionButtons">
              <Button variant="secondary" size="sm" type="button">
                Edit
              </Button>
              <Button
                variant="link"
                size="sm"
                type="button"
                className="text-danger deleteButton"
                aria-label={`Delete ${itemLabel.toLowerCase()} ${row.original.name}`}
              >
                <i className="fa fa-trash" />
              </Button>
            </div>
          </div>
        ),
      },
    ],
    [itemLabel]
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: nameFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const filteredRowCount = table.getFilteredRowModel().rows.length;

  return (
    <div className="featureFlagList assignmentTable">
      <FeatureFlagListToolbar
        pageSize={pagination.pageSize}
        searchValue={globalFilter}
        searchInputId={`${idPrefix}-search`}
        searchAriaLabel={`Search by ${itemLabel.toLowerCase()} name`}
        onPageSizeChange={(pageSize) =>
          setPagination({ pageIndex: 0, pageSize })
        }
        onSearchChange={(value) => {
          setGlobalFilter(value);
          setPagination((current) => ({ ...current, pageIndex: 0 }));
        }}
      />

      <Table responsive className="featureFlagTable table-striped mb-0">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();

                return (
                  <th
                    key={header.id}
                    className={canSort ? "sortableHeader" : undefined}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    aria-sort={
                      sorted === "asc"
                        ? "ascending"
                        : sorted === "desc"
                          ? "descending"
                          : canSort
                            ? "none"
                            : undefined
                    }
                  >
                    <span className="headerContent">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && (
                        <span className="sortIcons" aria-hidden="true">
                          <i className={`fa fa-caret-up${sorted === "asc" ? " is-active" : ""}`} />
                          <i className={`fa fa-caret-down${sorted === "desc" ? " is-active" : ""}`} />
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center text-muted py-3">
                No {itemLabel.toLowerCase()} assignments found.
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <FeatureFlagListPagination
        pageIndex={pagination.pageIndex}
        pageCount={table.getPageCount()}
        pageSize={pagination.pageSize}
        filteredRowCount={filteredRowCount}
        canPreviousPage={table.getCanPreviousPage()}
        canNextPage={table.getCanNextPage()}
        onPreviousPage={() => table.previousPage()}
        onNextPage={() => table.nextPage()}
        onGoToPage={(page) => table.setPageIndex(page)}
      />

      <div className="assignmentTableActions">
        <Button variant="primary" size="sm" type="button">
          Add {itemLabel}
        </Button>
      </div>
    </div>
  );
}

export default FeatureFlagAssignmentTable;
